import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { doc, onSnapshot, setDoc } from "firebase/firestore";
import {
  ChevronDown,
  ChevronRight,
  Volume2,
  Play,
  Music,
  ExternalLink,
  Sparkles,
} from "lucide-react";
import { db } from "../lib/firebase";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { cn } from "../lib/utils";
import {
  PHASES,
  PHASE_MAP,
  DIATONIC,
  REF_KEYS,
  RECIPES,
  STARTER_SONGS,
  GLOSSARY,
  ALL_CHECK_IDS,
  phaseCheckIds,
  chordsForProgression,
  notesFor,
  playChord,
  playProgression,
  audioSupported,
} from "../lib/keyboardPlan";

/* ============================================================
   Keyboard — an interactive lesson plan for learning to comp
   and play with other people. Progress (checked drills /
   milestones) is saved per device via an anonymous id and
   synced to Firestore at keyboardProgress/{userId}; it falls
   back to localStorage when Firebase isn't configured.
   ============================================================ */

const USER_KEY = "keyboardUserId";
const LOCAL_PROGRESS_KEY = "keyboardProgress"; // offline fallback

const newId = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

const getUserId = () => {
  let id = localStorage.getItem(USER_KEY);
  if (!id) {
    id = newId();
    localStorage.setItem(USER_KEY, id);
  }
  return id;
};

const CAN_PLAY = audioSupported();

// A small reusable "play this chord" button used in the reference tables.
const ChordButton = ({ chord, notes }) => (
  <button
    type="button"
    onClick={() => playChord(notes)}
    className="inline-flex items-center gap-1 rounded-md border border-input bg-background px-2 py-1 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground"
    title={notes.join("–")}
  >
    {chord}
  </button>
);

// One checkable line (a drill step or a "Done when" milestone).
const CheckRow = ({ id, checked, onToggle, children, milestone }) => (
  <label
    htmlFor={id}
    className="flex cursor-pointer items-start gap-3 py-1 text-sm leading-relaxed"
  >
    <Checkbox
      id={id}
      checked={checked}
      onCheckedChange={() => onToggle(id)}
      className="mt-0.5"
    />
    <span
      className={cn(
        milestone && "font-medium",
        checked && "text-muted-foreground line-through"
      )}
    >
      {milestone && <span className="text-primary">Done when: </span>}
      {children}
    </span>
  </label>
);

const Unit = ({ unit, checks, onToggle }) => (
  <div className="rounded-lg border bg-card/40 p-4">
    <h4 className="font-semibold">{unit.title}</h4>
    {unit.goal && (
      <p className="mt-1 text-sm">
        <span className="font-medium text-primary">Goal: </span>
        {unit.goal}
      </p>
    )}
    {unit.concept && (
      <p className="mt-2 text-sm text-muted-foreground">{unit.concept}</p>
    )}

    {unit.audio && CAN_PLAY && (
      <Button
        type="button"
        variant="secondary"
        size="sm"
        className="mt-3"
        onClick={() =>
          playProgression(chordsForProgression(unit.audio.key, unit.audio.degrees))
        }
      >
        <Play className="mr-1.5 h-3.5 w-3.5" />
        {unit.audio.label}
      </Button>
    )}

    {unit.drills && unit.drills.length > 0 && (
      <div className="mt-3">
        <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Drill
        </div>
        <div className="mt-1">
          {unit.drills.map((d, i) => {
            const id = `${unit.id}-drill-${i}`;
            return (
              <CheckRow key={id} id={id} checked={!!checks[id]} onToggle={onToggle}>
                {d}
              </CheckRow>
            );
          })}
        </div>
      </div>
    )}

    {unit.doneWhen && (
      <div className="mt-3 border-t pt-2">
        <CheckRow
          id={`${unit.id}-done`}
          checked={!!checks[`${unit.id}-done`]}
          onToggle={onToggle}
          milestone
        >
          {unit.doneWhen}
        </CheckRow>
      </div>
    )}
  </div>
);

const Phase = ({ phase, checks, onToggle, open, onToggleOpen }) => {
  const ids = useMemo(() => phaseCheckIds(phase), [phase]);
  const done = ids.filter((id) => checks[id]).length;
  const total = ids.length;

  return (
    <Card>
      <CardHeader
        className="cursor-pointer select-none"
        onClick={() => onToggleOpen(phase.id)}
      >
        <div className="flex items-center justify-between gap-3">
          <CardTitle className="flex items-center gap-2 text-xl">
            {open ? (
              <ChevronDown className="h-5 w-5 shrink-0" />
            ) : (
              <ChevronRight className="h-5 w-5 shrink-0" />
            )}
            {phase.title}
          </CardTitle>
          {total > 0 && (
            <span
              className={cn(
                "shrink-0 rounded-full px-2.5 py-0.5 text-xs font-medium",
                done === total
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted text-muted-foreground"
              )}
            >
              {done}/{total}
            </span>
          )}
        </div>
        <CardDescription className="pl-7">{phase.installs}</CardDescription>
      </CardHeader>
      {open && (
        <CardContent className="space-y-4">
          {phase.note && (
            <p className="rounded-md bg-muted/60 p-3 text-sm italic text-muted-foreground">
              {phase.note}
            </p>
          )}
          {phase.units.map((u) => (
            <Unit key={u.id} unit={u} checks={checks} onToggle={onToggle} />
          ))}
        </CardContent>
      )}
    </Card>
  );
};

/* ---- Ear-training panel -------------------------------------------------- */

const EAR_KEYS = ["C", "G", "D"];
const MOVES = [
  { label: "I–IV–I", degrees: ["I", "IV", "I"] },
  { label: "I–V–I", degrees: ["I", "V", "I"] },
  { label: "I–vi–I", degrees: ["I", "vi", "I"] },
];
const SINGLE = ["I", "IV", "V", "vi"];

const EarTrainer = () => {
  const [key, setKey] = useState("C");
  const [mode, setMode] = useState("moves"); // "moves" | "single"
  const [answer, setAnswer] = useState(null);
  const [picked, setPicked] = useState(null);
  const [score, setScore] = useState({ right: 0, total: 0 });

  const options = mode === "moves" ? MOVES.map((m) => m.label) : SINGLE;

  const playPrompt = useCallback(
    (a) => {
      if (mode === "moves") {
        const move = MOVES.find((m) => m.label === a);
        playProgression(chordsForProgression(key, move.degrees), 800);
      } else {
        playChord(notesFor(key, a), 1100);
      }
    },
    [key, mode]
  );

  const next = useCallback(() => {
    const a = options[Math.floor(Math.random() * options.length)];
    setAnswer(a);
    setPicked(null);
    playPrompt(a);
  }, [options, playPrompt]);

  const guess = (opt) => {
    if (picked || !answer) return;
    setPicked(opt);
    setScore((s) => ({ right: s.right + (opt === answer ? 1 : 0), total: s.total + 1 }));
  };

  const reset = (changes) => {
    setAnswer(null);
    setPicked(null);
    setScore({ right: 0, total: 0 });
    changes && changes();
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-xl">
          <Sparkles className="h-5 w-5" /> Ear-training drill
        </CardTitle>
        <CardDescription>
          Hear the key, then name what's moving. Guessing right ~70% of the time is
          enough to hang (Phase 5).
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Key + mode controls */}
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">Key</span>
            {EAR_KEYS.map((k) => (
              <Button
                key={k}
                size="sm"
                variant={key === k ? "default" : "outline"}
                onClick={() => reset(() => setKey(k))}
              >
                {k}
              </Button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">Mode</span>
            <Button
              size="sm"
              variant={mode === "moves" ? "default" : "outline"}
              onClick={() => reset(() => setMode("moves"))}
            >
              Chord moves
            </Button>
            <Button
              size="sm"
              variant={mode === "single" ? "default" : "outline"}
              onClick={() => reset(() => setMode("single"))}
            >
              Single chords
            </Button>
          </div>
        </div>

        {/* Listen helper + new prompt */}
        <div className="flex flex-wrap items-center gap-3">
          <Button
            variant="secondary"
            size="sm"
            onClick={() =>
              playProgression(chordsForProgression(key, ["IV", "V", "I"]), 800)
            }
          >
            <Volume2 className="mr-1.5 h-4 w-4" /> Hear home (cadence in {key})
          </Button>
          <Button size="sm" onClick={next}>
            <Play className="mr-1.5 h-4 w-4" /> {answer ? "New prompt" : "Start"}
          </Button>
          {answer && (
            <Button variant="ghost" size="sm" onClick={() => playPrompt(answer)}>
              Replay
            </Button>
          )}
          <span className="ml-auto text-sm text-muted-foreground">
            Score: {score.right}/{score.total}
          </span>
        </div>

        {/* Guess options */}
        {answer && (
          <div className="flex flex-wrap gap-2">
            {options.map((opt) => {
              const isAnswer = opt === answer;
              const isPicked = opt === picked;
              const showState = picked != null;
              return (
                <Button
                  key={opt}
                  variant="outline"
                  onClick={() => guess(opt)}
                  disabled={showState}
                  className={cn(
                    "min-w-[5rem]",
                    showState && isAnswer && "border-green-500 bg-green-500/15 text-green-700 dark:text-green-400",
                    showState && isPicked && !isAnswer &&
                      "border-red-500 bg-red-500/15 text-red-700 dark:text-red-400"
                  )}
                >
                  {opt}
                </Button>
              );
            })}
          </div>
        )}
        {picked && (
          <p className="text-sm font-medium">
            {picked === answer ? "✓ Right!" : `✗ It was ${answer}.`} Hit “New prompt”
            to keep going.
          </p>
        )}
      </CardContent>
    </Card>
  );
};

/* ---- Reference cards ----------------------------------------------------- */

const ReferenceCards = () => (
  <Card>
    <CardHeader>
      <CardTitle className="flex items-center gap-2 text-xl">
        <Music className="h-5 w-5" /> Reference cards
      </CardTitle>
      <CardDescription>
        Tap any chord to hear it. The quality of each degree is the same in every
        key.
      </CardDescription>
    </CardHeader>
    <CardContent className="space-y-6">
      {REF_KEYS.map((k) => (
        <div key={k}>
          <div className="mb-2 text-sm font-semibold">Key of {k}</div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[34rem] border-collapse text-sm">
              <thead>
                <tr className="text-muted-foreground">
                  {DIATONIC[k].map((c) => (
                    <th key={c.deg} className="border-b p-2 text-center font-medium">
                      {c.deg}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr>
                  {DIATONIC[k].map((c) => (
                    <td key={c.deg} className="p-2 text-center">
                      <ChordButton chord={c.chord} notes={c.notes} />
                    </td>
                  ))}
                </tr>
                <tr className="text-xs text-muted-foreground">
                  {DIATONIC[k].map((c) => (
                    <td key={c.deg} className="px-2 pb-2 text-center">
                      {c.notes.join("-")}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
          {CAN_PLAY && (
            <Button
              variant="secondary"
              size="sm"
              className="mt-2"
              onClick={() =>
                playProgression(
                  chordsForProgression(k, ["I", "ii", "iii", "IV", "V", "vi", "vii°"])
                )
              }
            >
              <Play className="mr-1.5 h-3.5 w-3.5" /> Play the run
            </Button>
          )}
        </div>
      ))}

      <div className="grid gap-3 sm:grid-cols-2">
        {RECIPES.map((r) => (
          <div key={r.title} className="rounded-lg border bg-muted/40 p-3">
            <div className="text-sm font-semibold">{r.title}</div>
            <div className="text-sm text-muted-foreground">{r.text}</div>
          </div>
        ))}
      </div>
    </CardContent>
  </Card>
);

/* ---- Starter songs ------------------------------------------------------- */

const SongRow = ({ song }) => {
  const [open, setOpen] = useState(false);
  const searchUrl = `https://open.spotify.com/search/${encodeURIComponent(
    `${song.title} ${song.artist}`
  )}`;
  return (
    <div className="rounded-lg border p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="font-semibold">{song.title}</div>
          <div className="text-sm text-muted-foreground">
            {song.artist} · Key of {song.key} · {song.chords} ·{" "}
            <span className="font-medium">{song.numbers.join("–")}</span>
          </div>
          <div className="text-xs text-muted-foreground">{song.teaches}</div>
        </div>
        <div className="flex items-center gap-2">
          {CAN_PLAY && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() =>
                playProgression(chordsForProgression(song.key, song.numbers), 900)
              }
            >
              <Play className="mr-1.5 h-3.5 w-3.5" /> Changes
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => setOpen((o) => !o)}>
            {open ? "Hide" : "Play along"}
          </Button>
        </div>
      </div>
      {open && (
        <div className="mt-3">
          {song.spotifyId ? (
            <iframe
              title={`${song.title} on Spotify`}
              src={`https://open.spotify.com/embed/track/${song.spotifyId}`}
              width="100%"
              height="152"
              frameBorder="0"
              allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
              loading="lazy"
              className="rounded-xl"
            />
          ) : (
            <a
              href={searchUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
            >
              <ExternalLink className="h-4 w-4" /> Search “{song.title}” on Spotify
            </a>
          )}
        </div>
      )}
    </div>
  );
};

const StarterSongs = () => (
  <Card>
    <CardHeader>
      <CardTitle className="text-xl">Starter songs</CardTitle>
      <CardDescription>
        Run the master loop on each: chords under hands → play along with the
        recording → say the numbers out loud → next song.
      </CardDescription>
    </CardHeader>
    <CardContent className="space-y-3">
      {STARTER_SONGS.map((s) => (
        <SongRow key={s.title} song={s} />
      ))}
    </CardContent>
  </Card>
);

/* ---- Page ---------------------------------------------------------------- */

const Keyboard = () => {
  const [userId] = useState(getUserId);
  const [checks, setChecks] = useState({});
  const checksRef = useRef(checks);
  checksRef.current = checks;

  // Phases 0 and 1 start open; the rest collapsed.
  const [openPhases, setOpenPhases] = useState({ 0: true, 1: true });

  const docRef = useMemo(
    () => (db ? doc(db, "keyboardProgress", userId) : null),
    [userId]
  );

  // Live sync from Firestore, or load once from localStorage when offline.
  useEffect(() => {
    if (!docRef) {
      try {
        const saved = localStorage.getItem(LOCAL_PROGRESS_KEY);
        if (saved) setChecks(JSON.parse(saved) || {});
      } catch {
        /* ignore */
      }
      return undefined;
    }
    const unsub = onSnapshot(
      docRef,
      (snap) => {
        const d = snap.exists() ? snap.data() : {};
        setChecks(d.checks || {});
      },
      () => {}
    );
    return unsub;
  }, [docRef]);

  const toggle = useCallback(
    (id) => {
      const next = { ...checksRef.current, [id]: !checksRef.current[id] };
      setChecks(next);
      try {
        localStorage.setItem(LOCAL_PROGRESS_KEY, JSON.stringify(next));
      } catch {
        /* ignore quota / private-mode */
      }
      if (docRef) {
        setDoc(docRef, { checks: next, updatedAt: Date.now() }, { merge: true }).catch(
          () => {}
        );
      }
    },
    [docRef]
  );

  const toggleOpen = useCallback(
    (id) => setOpenPhases((o) => ({ ...o, [id]: !o[id] })),
    []
  );

  const doneCount = ALL_CHECK_IDS.filter((id) => checks[id]).length;
  const pct = ALL_CHECK_IDS.length
    ? Math.round((doneCount / ALL_CHECK_IDS.length) * 100)
    : 0;

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mx-auto w-full max-w-4xl space-y-6">
        {/* Header + overall progress */}
        <div className="space-y-3">
          <h1 className="text-3xl font-bold">Keyboard for Playing With People</h1>
          <p className="text-muted-foreground">
            A lesson plan for sitting down with other musicians and playing rock /
            pop / folk / indie together — by chart and by ear, in a support role.
            Check things off as you go; your progress is saved.
          </p>
          <div className="space-y-1">
            <div className="flex items-center justify-between text-sm font-medium">
              <span>Overall progress</span>
              <span>
                {doneCount}/{ALL_CHECK_IDS.length} · {pct}%
              </span>
            </div>
            <div className="h-2.5 w-full overflow-hidden rounded-full bg-muted">
              <div
                className="h-full rounded-full bg-primary transition-all"
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>
          <a
            href="/number-sense"
            className="flex items-center gap-3 rounded-lg border bg-card p-4 transition-colors hover:bg-accent"
          >
            <Sparkles className="h-5 w-5 shrink-0 text-primary" />
            <div className="text-sm">
              <div className="font-semibold">
                Want to drill this like a game? → Number Sense
              </div>
              <div className="text-muted-foreground">
                An interactive, gamified Nashville Number System trainer — translate
                numbers to chords, build them on a keyboard, and test your ear.
              </div>
            </div>
            <ChevronRight className="ml-auto h-5 w-5 shrink-0 text-muted-foreground" />
          </a>
        </div>

        {/* The map */}
        <Card>
          <CardHeader>
            <CardTitle className="text-xl">The map</CardTitle>
            <CardDescription>The whole thing at a glance.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[34rem] border-collapse text-sm">
                <thead>
                  <tr className="text-left text-muted-foreground">
                    <th className="border-b p-2">Phase</th>
                    <th className="border-b p-2">Units</th>
                    <th className="border-b p-2">What it installs</th>
                  </tr>
                </thead>
                <tbody>
                  {PHASE_MAP.map((row) => (
                    <tr key={row.phase}>
                      <td className="border-b p-2 font-medium">{row.phase}</td>
                      <td className="border-b p-2">{row.units}</td>
                      <td className="border-b p-2 text-muted-foreground">
                        {row.installs}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Phases */}
        {PHASES.map((phase) => (
          <Phase
            key={phase.id}
            phase={phase}
            checks={checks}
            onToggle={toggle}
            open={!!openPhases[phase.id]}
            onToggleOpen={toggleOpen}
          />
        ))}

        {/* Ear trainer */}
        {CAN_PLAY && <EarTrainer />}

        {/* Reference cards */}
        <ReferenceCards />

        {/* Starter songs */}
        <StarterSongs />

        {/* Glossary */}
        <Card>
          <CardHeader>
            <CardTitle className="text-xl">Glossary</CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="space-y-2 text-sm">
              {GLOSSARY.map((g) => (
                <div key={g.term}>
                  <dt className="inline font-semibold">{g.term}: </dt>
                  <dd className="inline text-muted-foreground">{g.def}</dd>
                </div>
              ))}
            </dl>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Keyboard;
